import CreateButton from "../components/CreateButton"
import { useCallback, useEffect, useRef, useState } from "react"
import Dropzone from "react-dropzone"
import { Helmet, HelmetProvider } from "react-helmet-async"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"
import { Line, LineChart, ResponsiveContainer, Tooltip } from "recharts"
import TextareaAutosize from 'react-textarea-autosize'
import api from "../api"
import { ReactComponent as EditIcon } from "../assets/images/icons/ic-image-edit.svg"
import { ReactComponent as StopIcon } from "../assets/images/icons/ic-stop.svg"
import { ReactComponent as UnlockIcon } from "../assets/images/icons/ic-unlock.svg"
import { ReactComponent as InsertImageIllustration } from "../assets/images/illustrations/il-insert-image.svg"
import { ReactComponent as DeleteIcon } from "../assets/images/icons/ic-delete.svg"

import UserPlaceholder from "../assets/images/placeholders/user-placeholder.png"
import { AccountStatus, ButtonStatus } from "../common/constants"
import { Each } from "../common/Each"
import Breadcrumb from "../components/Breadcrumb"
import Button from "../components/Button"
import AlertDialog from "../components/dialogs/AlertDialog"
import Dialog from "../components/dialogs/Dialog"
import TagSelector from "../components/TagSelector"
import ImageCropper from "../components/ImageCropper"
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout"
import Skeleton from "../components/Skeleton"
import TextInput from "../components/TextInput"
import { DialogStatus } from "../enums"
import { capitalize } from "../utils"
import styles from "./Teacher.module.css"
import Card from "../components/cards/Card"
import MeatBallsMenu from "../components/MeatBallsMenu"
import Select from "../components/Select"
import MultipleSelect from "../components/MultipleSelect"

const Teacher = () => {
  const { t } = useTranslation()
  const { teacherId, tutorId } = useParams()
  const id = teacherId ?? tutorId

  const [teacher, setTeacher] = useState(null)
  const [teacherLoading, setTeacherLoading] = useState(false)
  const [profilePic, setProfilePic] = useState(null)
  const [name, setName] = useState("")
  const [surname, setSurname] = useState("")
  const [email, setEmail] = useState("")
  const [qualification, setQualification] = useState("")
  const [bio, setBio] = useState("")
  const [tagsList, setTagList] = useState([])
  const [tags, setTags] = useState([])
  const [options, setOptions] = useState([])
  const [hasChanges, setHasChanges] = useState(false)
  const [phone, setPhone] = useState("")

  const [dialogOpen, setDialogOpen] = useState(false)
  const [imgFile, setImgFile] = useState(null)
  const [cropMode, setCropMode] = useState(false)
  const [alert, setAlert] = useState({
    open: false,
    title: "",
    message: "",
    status: DialogStatus.Default,
    actions: []
  })

  const [periodDays, setPeriodDays] = useState(30)
  const [loadingStats, setLoadingStats] = useState(false)
  const [stats, setStats] = useState({
    period_days: 30,
    lessons_done: 0,
    effective_lesson_seconds: 0,
    average_session_seconds: 0,
    average_students_per_session: 0,
    students_in_period: 0,
    average_attendance_rate_percent: 0,
    attendance_rate_trend_by_day: [],
    average_participation_seconds: 0,
    returning_students_rate_percent: 0,
    early_dropout_rate_percent: 0,
    early_dropout_rate_trend_by_day: [],
    lesson_feedbacks_count: 0,
    positive_feedbacks_count: 0,
    neutral_feedbacks_count: 0,
    negative_feedbacks_count: 0,
    average_lesson_feedback_score: null,
    average_lesson_feedback_score_by_question: [],
    worst_lessons_feedback: [],
    feedback_trend_by_day: [],
    average_start_delta_seconds: 0,
    average_end_delta_seconds: 0,
  })
  const [coursesModules, setCoursesModules] = useState([]) // [{course, modules:[{...module, edition:{}}]}]
  const [expandedEditions, setExpandedEditions] = useState({})
  const [moduleDialog, setModuleDialog] = useState(false)

  const [savingStatus, setSavingStatus] = useState(null)

  const dropzoneRef = useRef(null)

  const [courses, setCourses] = useState([])
  const [loadingCourses, setLoadingCourses] = useState(false)
  const [editions, setEditions] = useState([])
  const [modules, setModules] = useState([])
  const [selectedModules, setSelectedModules] = useState([])
  const [loadingEditions, setLoadingEditions] = useState(false)
  const [loadingModules, setLoadingModules] = useState(false)
  const [subscriptions, setSubscriptions] = useState({ course: { id: null, prefilled: false }, module: { id: null, prefilled: false }, edition: { id: null, prefilled: false } })

  const formatDuration = useCallback((seconds) => {
    const totalSeconds = Math.max(0, Math.round(Number(seconds || 0)))
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const remainingSeconds = totalSeconds % 60
    return `${hours}h ${minutes}m ${remainingSeconds}s`
  }, [])

  const formatDeltaMinutes = useCallback((seconds) => {
    const safeSeconds = Number(seconds || 0)
    const minutes = Math.round(Math.abs(safeSeconds) / 60)
    if (safeSeconds > 0) return `${minutes} min in ritardo`
    if (safeSeconds < 0) return `${minutes} min in anticipo`
    return "in orario"
  }, [])

  const formatPercent = useCallback((value) => {
    const safeValue = Number(value || 0)
    return `${safeValue.toFixed(1)}%`
  }, [])

  const formatScore = useCallback((value) => {
    if (value === null || value === undefined) return "-"
    return `${Number(value).toFixed(1)} / 5`
  }, [])

  const getScoreClass = useCallback((value) => {
    const score = Number(value)
    if (!Number.isFinite(score)) return ""
    if (score > 4) return styles.scoreHigh
    if (score >= 3) return styles.scoreMedium
    return styles.scoreLow
  }, [])

  const formatDayLabel = useCallback((value) => {
    if (!value) return ""
    const date = new Date(`${value}T00:00:00`)
    return date.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" })
  }, [])

  const formatLongDayLabel = useCallback((value) => {
    if (!value) return ""
    const date = new Date(`${value}T00:00:00`)
    return date.toLocaleDateString("it-IT", { day: "numeric", month: "long" })
  }, [])

  const enrichSeriesWithMovingAverage = useCallback((series, key, windowSize) => {
    const source = series ?? []
    return source.map((item, index) => {
      if (index + 1 < windowSize) {
        return { ...item, moving_average: null }
      }

      const windowValues = source
        .slice(index - windowSize + 1, index + 1)
        .map((entry) => Number(entry?.[key]))

      if (windowValues.some((value) => !Number.isFinite(value))) {
        return { ...item, moving_average: null }
      }

      const average = windowValues.reduce((sum, value) => sum + value, 0) / windowValues.length
      return { ...item, moving_average: average }
    })
  }, [])

  const getTrendDirection = useCallback((series, key, epsilon = 0.12, windowSize = 3) => {
    const source = series ?? []
    if (source.length === 0) return null

    const lastValue = Number(source[source.length - 1]?.[key])
    if (!Number.isFinite(lastValue)) return null

    const values = source
      .map((item) => Number(item?.[key]))
      .filter((value) => Number.isFinite(value))

    if (values.length < (windowSize + 1)) return null

    const smoothed = []
    for (let i = 0; i <= values.length - windowSize; i += 1) {
      const windowValues = values.slice(i, i + windowSize)
      const avg = windowValues.reduce((sum, value) => sum + value, 0) / windowValues.length
      smoothed.push(avg)
    }

    if (smoothed.length < 2) return null

    const n = smoothed.length
    let sumX = 0
    let sumY = 0
    let sumXY = 0
    let sumXX = 0

    smoothed.forEach((value, index) => {
      sumX += index
      sumY += value
      sumXY += index * value
      sumXX += index * index
    })

    const denominator = (n * sumXX) - (sumX * sumX)
    if (denominator === 0) return null

    const slope = ((n * sumXY) - (sumX * sumY)) / denominator
    if (Math.abs(slope) <= epsilon) return "flat"
    return slope > 0 ? "up" : "down"
  }, [])

  const getModules = useCallback(async () => {
    try {
      const modules = await api.get(`/admin/teachers/${id}/modules`)
      // Group by course id
      const grouped = {}
      modules.forEach(m => {
        const c = m.course
        if (!c) return
        // ensure key
        if (!grouped[c.id]) {
          grouped[c.id] = { course: c, modules: [] }
        }
        grouped[c.id].modules.push(m)
      })
      setCoursesModules(Object.values(grouped))
    } catch (e) {
      console.error(e)
    }
  }, [id])

  useEffect(() => {
    const getTeacher = async () => {
      setTeacherLoading(true)
      try {
        const teacher = await api.get(`/admin/teachers/${id}`)
        setTeacher(teacher)
        setTeacherLoading(false)
        return teacher
      } catch (e) {
        setTeacherLoading(false)
        console.error(e)
      }
    }

    const getTags = async (teacher) => {
      try {
        const tags = await api.get(`/admin/tags?type=general`)

        setTagList(tags)
        setOptions(tags.filter((t) => {
          return !(teacher.tags && teacher.tags.map(lt => lt.id).includes(t.id))
        }))
      } catch (e) {
        console.error(e)
      }
    }

    const getCourses = async () => {
      setLoadingCourses(true)
      try {
        const response = await api.get(`/admin/courses`)
        const coursesList = Array.isArray(response)
          ? response
          : Array.isArray(response?.courses)
            ? response.courses
            : []

        setCourses(coursesList)
      } catch (e) {
        console.error(e)
      } finally {
        setLoadingCourses(false)
      }
    }

    getTeacher().then((t) => {
      getTags(t)
    })

    getModules()
    getCourses()
  }, [id, getModules])

  useEffect(() => {
    if (teacher) {
      setName(teacher.name)
      setSurname(teacher.surname)
      setEmail(teacher.email)
      setQualification(teacher.qualification)
      setBio(teacher.description ?? '')
      setTags(teacher.tags)
      setPhone(teacher.phone)
    }
  }, [teacher])

  useEffect(() => {
    if (teacher) {
      setHasChanges(
        (email !== teacher.email) ||
        (name !== teacher.name) ||
        (surname !== teacher.surname) ||
        (phone !== (teacher.phone ?? "")) ||
        (qualification !== teacher.qualification) ||
        (bio !== teacher.description) ||
        (JSON.stringify(tags) !== JSON.stringify(teacher.tags)) ||
        ((profilePic && profilePic !== teacher.profilePic) ?? false)
      )
    }
  }, [teacher, name, surname, qualification, bio, tags, profilePic, email, phone])

  useEffect(() => {
    const newOptions = tagsList.filter((tl) => {
      return tags.map(t => t.id).includes(tl.id) === false
    })
    setOptions(newOptions)
  }, [tags, tagsList])

  useEffect(() => {
    const getProfileStatistics = async () => {
      setLoadingStats(true)
      try {
        const response = await api.get(`/admin/teachers/${id}/profile/statistics?days=${periodDays}`)
        setStats(response)
      } catch (e) {
        console.error(e)
      } finally {
        setLoadingStats(false)
      }
    }

    getProfileStatistics()
  }, [id, periodDays])

  const saveChanges = async () => {
    setSavingStatus(ButtonStatus.Loading)
    try {
      const formData = new FormData()
      formData.append("name", name)
      formData.append("surname", surname)
      formData.append("email", email)
      formData.append("qualification", qualification)
      formData.append("description", bio)
      formData.append("phone", phone)
      formData.append("tags", JSON.stringify(tags.map(t => t.id)))

      if (profilePic) {
        let blob = await fetch(profilePic).then(r => r.blob());
        formData.append("picture", blob, `${teacher.id}-picture.jpg`);
      }

      try {
        const teacher = await api.put(`/admin/teachers/${id}`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        setTeacher(teacher)
      } catch (e) {
        throw new Error("Non è stato possibile salvare le modifiche.")
      }

      // setDialogOpen(true)
      setAlert({ open: true, title: t('profile.saved'), message: t('profile.changesSaved'), status: DialogStatus.Success })
      setSavingStatus(ButtonStatus.Success)
    } catch (e) {
      console.error(e)
      setSavingStatus(ButtonStatus.Error)
    }
  }

  const resetAlert = useCallback(() => {
    setAlert({
      open: false,
      title: "",
      message: "",
      status: DialogStatus.Default,
      actions: []
    })
  }, [])

  const changeAccountStatus = useCallback(async (status) => {
    setAlert(a => { return { ...a, status: DialogStatus.Loading } })

    try {
      const teacher = await api.patch(`/admin/teachers/${id}`, {
        status
      })
      setTeacher(teacher)
      setAlert(a => { return { ...a, status: DialogStatus.Success } })
    } catch (e) {
      console.error(e)
      setAlert(a => { return { ...a, status: DialogStatus.Error } })
    }
  }, [id])

  const [loadingAssign, setLoadingAssign] = useState(false)

  const sortModulesByPosition = (a, b) => {
    const aPos = Number.isFinite(Number(a?.position)) ? Number(a.position) : Number.MAX_SAFE_INTEGER
    const bPos = Number.isFinite(Number(b?.position)) ? Number(b.position) : Number.MAX_SAFE_INTEGER

    if (aPos !== bPos) return aPos - bPos
    return (a?.name ?? "").localeCompare(b?.name ?? "")
  }

  const toggleEdition = (courseId, editionId) => {
    const key = `${courseId}-${editionId}`
    setExpandedEditions((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const loadEditionsByCourse = useCallback(async (courseId) => {
    if (!courseId) {
      setEditions([])
      return
    }

    setLoadingEditions(true)
    try {
      const response = await api.get(`/admin/courses/${courseId}/editions`)
      const editionsList = (Array.isArray(response) ? response : []).map((editionItem) => ({ ...editionItem, course_id: courseId }))
      setEditions(editionsList)
    } catch (e) {
      console.error(e)
      setEditions([])
    } finally {
      setLoadingEditions(false)
    }
  }, [])

  const loadModulesByEdition = useCallback(async (courseId, editionId) => {
    if (!(courseId && editionId)) {
      setModules([])
      return []
    }

    setLoadingModules(true)
    try {
      const response = await api.get(`/admin/courses/${courseId}/editions`)
      const editionsList = Array.isArray(response) ? response : []
      const selectedEdition = editionsList.find((editionItem) => editionItem.id === editionId)
      const modulesList = (Array.isArray(selectedEdition?.modules) ? selectedEdition.modules : []).map((moduleItem) => ({
        ...moduleItem,
        edition_id: editionId,
      }))

      setModules(modulesList)
      return modulesList
    } catch (e) {
      console.error(e)
      setModules([])
      return []
    } finally {
      setLoadingModules(false)
    }
  }, [])

  const assignToModule = async () => {
    setLoadingAssign(true)
    try {
      await api.put(`/admin/teachers/${id}/modules`, {
        module_ids: selectedModules.map((m) => m.id),
        edition_id: subscriptions.edition.id,
      })
      setModuleDialog(false)
      setSubscriptions({ course: { id: null, prefilled: false }, module: { id: null, prefilled: false }, edition: { id: null, prefilled: false } })
      setSelectedModules([])
      setEditions([])
      setModules([])
      await getModules()
    }
    catch (e) {
      console.error(e)
    }
    setLoadingAssign(false)
  }

  const unassignFromModule = async (module_id) => {
    try {
      await api.delete(`/admin/teachers/${id}/modules/${module_id}`)
      await getModules()
    }
    catch (e) {
      console.error(e)
    }
  }

  const feedbackTrendData = (stats.feedback_trend_by_day ?? []).map((item) => ({
    ...item,
    average_score: Number(item?.feedback_count || 0) > 0 ? Number(item?.average_score) : null,
    label: formatDayLabel(item.date),
  }))

  const attendanceTrendData = (stats.attendance_rate_trend_by_day ?? []).map((item) => ({
    ...item,
    label: formatDayLabel(item.date),
  }))

  const dropoutTrendData = (stats.early_dropout_rate_trend_by_day ?? []).map((item) => ({
    ...item,
    label: formatDayLabel(item.date),
  }))

  const trendWindowSize = periodDays <= 7 ? 3 : periodDays <= 30 ? 7 : 15

  const feedbackTrendChartData = enrichSeriesWithMovingAverage(feedbackTrendData, "average_score", trendWindowSize)
  const attendanceTrendChartData = enrichSeriesWithMovingAverage(attendanceTrendData, "average_rate_percent", trendWindowSize)
  const dropoutTrendChartData = enrichSeriesWithMovingAverage(dropoutTrendData, "average_rate_percent", trendWindowSize)

  const attendanceTrendDirection = getTrendDirection(attendanceTrendData, "average_rate_percent", 0.1, trendWindowSize)
  const dropoutTrendDirection = getTrendDirection(dropoutTrendData, "average_rate_percent", 0.1, trendWindowSize)
  const feedbackTrendDirection = getTrendDirection(feedbackTrendData, "average_score", 0.05, trendWindowSize)


  return (
    <HeaderFooterLayout>
      <HelmetProvider>
        <Helmet>
          <title>{teacher ? `${teacher.name} ${teacher.surname} | Docente` : "Docente"}</title>
        </Helmet>
      </HelmetProvider>
      <div className={styles.container}>
        <div className={styles.section}>
          <div className={styles.sectionInner}>
            <Breadcrumb items={[{label: 'Utenti', to: '/users'}, {label: tutorId ? 'Tutor' : 'Docenti', to: tutorId ? '/users/tutors' : '/users/teachers'}, {label: teacher ? `${teacher.name} ${teacher.surname}` : 'Profilo'}]} />
            <header className={styles.pageHeader}><h1>{tutorId ? 'Profilo tutor' : 'Profilo docente'}</h1><p>Gestisci i dati del profilo, i corsi assegnati e le statistiche didattiche.</p></header>
            <div className={styles.content}>
              <div className={styles.leftColumn}>
                <div className={styles.teacher}>
                  <img className={styles.profilePic} src={(profilePic || teacher?.picture) ?? UserPlaceholder} alt='' />
                  {
                    teacherLoading &&
                    <Skeleton type="rect" width="164px" height="24px" borderRadius={"12px"} />
                  }
                  {
                    !teacherLoading &&
                    <div className={styles.bio}>
                      <div className={styles.username}>{`${teacher?.name} ${teacher?.surname}`}</div>
                      <div className={styles.email}>{teacher?.email}</div>
                    </div>

                  }
                  <Button
                    appearance="subtle" accentColor="var(--primary)"
                    onClick={() => {
                      setDialogOpen(true)
                    }}
                  >
                    <EditIcon />
                    Cambia foto
                  </Button>
                  {
                    teacher?.status === AccountStatus.Active &&
                    <Button
                      appearance="subtle" accentColor="var(--sf-red)"
                      onClick={() => {
                        setAlert({
                          open: true,
                          title: "Sospensione docente",
                          message: "Il docente non potrà più accedere alla piattaforma docenti. Sei sicuro di voler procedere?",
                          status: DialogStatus.Default,
                          actions: [
                            {
                              label: "ANNULLA",
                              onClick: resetAlert,
                              inverse: true
                            },
                            {
                              label: "SI, SOSPENDI",
                              onClick: () => changeAccountStatus(AccountStatus.Suspended),
                            }
                          ]
                        })
                      }}
                    >
                      <StopIcon style={{ transform: "rotate(-45deg)" }} />
                      Sospendi
                    </Button>
                  }

                  {
                    teacher?.status === AccountStatus.Suspended &&
                    <Button
                      accentColor={"var(--primary)"}
                      onClick={() => {
                        setAlert({
                          open: true,
                          title: "Attivazione docente",
                          message: "Il docente potrà accedere alla piattaforma docenti. Sei sicuro di voler procedere?",
                          status: DialogStatus.Default,
                          actions: [
                            {
                              label: "ANNULLA",
                              onClick: resetAlert,
                              inverse: true
                            },
                            {
                              label: "SI, ATTIVA",
                              onClick: () => changeAccountStatus(AccountStatus.Active),
                            }
                          ]
                        })
                      }}
                    >
                      <UnlockIcon />
                      Attiva
                    </Button>
                  }

                </div>
              </div>
              <div className={styles.rightColumn}>
                <div className={styles.card}>
                  <div className={styles.cardTitle}>{t("profile.editTitle")}</div>
                  <div className={styles.inputs}>
                    <div className={styles.row}>
                      <TextInput appearance="modern" value={name} type="text" label={capitalize(t('profile.name'))} placeholder={capitalize(t('profile.name'))} onKeyUp={v => setName(v)}></TextInput>
                      <TextInput appearance="modern" value={surname} type="text" label={capitalize(t('profile.surname'))} placeholder={capitalize(t('profile.surname'))} onKeyUp={v => setSurname(v)}></TextInput>
                    </div>
                    <div className={styles.row}>
                      <TextInput appearance="modern" value={email} type="text" label={capitalize(t('profile.email'))} placeholder={capitalize(t('profile.email'))} onKeyUp={v => setEmail(v)} />
                      <TextInput appearance="modern" value={phone} type="text" label={capitalize(t('profile.phone'))} placeholder={capitalize(t('profile.phone'))} onKeyUp={v => setPhone(v)} />
                    </div>
                    <TextInput appearance="modern" value={qualification} type="text" label={capitalize(t('profile.qualification'))} placeholder={capitalize(t('profile.qualification'))} onKeyUp={v => setQualification(v)} />
                    <label className={styles.field}><span className={styles.fieldLabel}>Biografia</span><TextareaAutosize
                      value={bio}
                      minRows={3} maxRows={10}
                      type="text"
                      placeholder={capitalize(t("profile.bio"))}
                      className={styles.textArea}
                      onChange={e => setBio(e.target.value)} /></label>

                    <div className={styles.field}><span className={styles.fieldLabel}>Competenze</span><TagSelector options={options} selected={tags} onAdd={tag => setTags(current => [...current, tag])} onRemove={tag => setTags(current => current.filter(item => item.id !== tag.id))} /></div>
                  </div>
                  <div style={{ alignSelf: "flex-end" }}>
                    <Button
                      status={savingStatus}
                      accentColor={"var(--primary)"}
                      style={{ padding: ".65rem 1rem", borderRadius: 8, fontSize: ".75rem" }}
                      disabled={hasChanges === false || savingStatus === ButtonStatus.Loading}
                      onClick={saveChanges}
                    >
                      Salva modifiche
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <Card title="Corsi assegnati">
              <CreateButton onClick={() => setModuleDialog(true)} style={{ position: 'absolute', top: '1rem', right: '1rem' }}>
                Aggiungi
              </CreateButton>
              <div className={styles.coursesContainer}>
                {coursesModules.length === 0 &&
                  <div className={styles.emptyLabel}>Nessun modulo assegnato</div>
                }
                <Each of={coursesModules.sort((a, b) => a.course.name.localeCompare(b.course.name))} render={(cm) => {
                  const modulesByEdition = cm.modules.reduce((acc, moduleItem) => {
                    const editionId = moduleItem.edition?.id
                    const editionName = moduleItem.edition?.name ?? "Edizione"
                    const key = editionId ?? `no-edition-${moduleItem.id}`

                    if (!acc[key]) {
                      acc[key] = {
                        id: editionId,
                        name: editionName,
                        modules: [],
                      }
                    }
                    acc[key].modules.push(moduleItem)
                    return acc
                  }, {})

                  const editionsGroups = Object.values(modulesByEdition).sort((a, b) => a.name.localeCompare(b.name))

                  return (
                    <div className={styles.course}>
                      <div className={styles.courseTitle}>{cm.course.name}</div>
                      <Each of={editionsGroups} render={(editionGroup) => {
                        const expansionKey = `${cm.course.id}-${editionGroup.id}`
                        const isExpanded = !!expandedEditions[expansionKey]

                        return (
                          <div className={styles.editionGroup}>
                            <button type="button" className={styles.editionHeader} onClick={() => toggleEdition(cm.course.id, editionGroup.id)}>
                              <div className={styles.editionTitle}>{editionGroup.name}</div>
                              <div className={styles.editionMeta}>{editionGroup.modules.length} moduli {isExpanded ? "-" : "+"}</div>
                            </button>
                            {isExpanded &&
                              <div className={styles.editionModules}>
                                <Each of={editionGroup.modules.sort(sortModulesByPosition)} render={(m) => {
                                  return (
                                    <div className={styles.module}>
                                      <div className={styles.moduleName}>{m.position != null ? `${Number(m.position) + 1}. ` : ""}{m.name}</div> <MeatBallsMenu actions={[
                                        {
                                          label: "Rimuovi",
                                          style: { color: "var(--sf-red)" },
                                          icon: DeleteIcon,
                                          onClick: () => {
                                            setAlert({
                                              open: true,
                                              title: "Rimuovi modulo",
                                              message: `Sei sicuro di voler rimuovere il docente dal modulo "${m.name}"?`,
                                              status: DialogStatus.Default,
                                              actions: [
                                                {
                                                  label: "ANNULLA",
                                                  onClick: resetAlert,
                                                  inverse: true
                                                },
                                                {
                                                  label: "RIMUOVI",
                                                  accentColor: 'var(--sf-red)',
                                                  onClick: async () => {
                                                    await unassignFromModule(m.id)
                                                    resetAlert()
                                                  }
                                                }
                                              ]
                                            })
                                          }
                                        }
                                      ]} />
                                    </div>
                                  )
                                }} />
                              </div>
                            }
                          </div>
                        )
                      }} />
                    </div>
                  )
                }} />
              </div>
            </Card>


            <div className={styles.statsContainer}>
              <div className={styles.card}>
                <div className={styles.header}>
                  <div className={styles.subtitle}>{t("profile.statistics.title").toUpperCase()}</div>
                </div>

                <div className={styles.filterRow}>
                  <Each
                    of={[7, 30, 90]}
                    render={(days) => (
                      <button
                        className={`${styles.filterButton} ${periodDays === days ? styles.active : ""}`}
                        onClick={() => setPeriodDays(days)}
                      >
                        {days}g
                      </button>
                    )}
                  />
                </div>

                {
                  loadingStats === true &&
                  <div className={styles.metricsGrid}>
                    <Each
                      of={[1, 2, 3, 4, 5, 6, 7, 8]}
                      render={() => (
                        <div className={styles.metricCardSkeleton}>
                          <Skeleton type="rect" width="100%" height="56px" borderRadius={"8px"} />
                        </div>
                      )}
                    />
                  </div>
                }

                {
                  loadingStats === false &&
                  <div className={styles.statsSections}>
                    <div className={styles.statsSection}>
                      <div className={styles.sectionTitle}>Lezioni</div>
                      <div className={styles.metricsGrid}>
                        <div className={styles.metricCard}>
                          <div className={styles.metricLabel}>Lezioni effettuate</div>
                          <div className={styles.metricValue}>{stats.lessons_done ?? 0}</div>
                        </div>

                        <div className={styles.metricCard}>
                          <div className={styles.metricLabel}>Ore di lezione effettive</div>
                          <div className={styles.metricValue}>{formatDuration(stats.effective_lesson_seconds)}</div>
                        </div>

                        <div className={styles.metricCard}>
                          <div className={styles.metricLabel}>Media delle sessioni</div>
                          <div className={styles.metricValue}>{formatDuration(stats.average_session_seconds)}</div>
                        </div>

                        <div className={styles.metricCard}>
                          <div className={styles.metricLabel}>Scostamento medio inizio lezione</div>
                          <div className={styles.metricValue}>{formatDeltaMinutes(stats.average_start_delta_seconds)}</div>
                        </div>

                        <div className={styles.metricCard}>
                          <div className={styles.metricLabel}>Scostamento medio fine lezione</div>
                          <div className={styles.metricValue}>{formatDeltaMinutes(stats.average_end_delta_seconds)}</div>
                        </div>
                      </div>
                    </div>

                    <div className={styles.statsSection}>
                      <div className={styles.sectionTitle}>Studenti</div>
                      <div className={styles.metricsGrid}>
                        <div className={styles.metricCard}>
                          <div className={styles.metricLabel}>Numero medio studenti/sessione</div>
                          <div className={styles.metricValue}>{stats.average_students_per_session ?? 0}</div>
                        </div>

                        <div className={styles.metricCard}>
                          <div className={styles.metricLabel}>Studenti nel periodo</div>
                          <div className={styles.metricValue}>{stats.students_in_period ?? 0}</div>
                        </div>

                        <div className={styles.metricCard}>
                          <div className={styles.metricLabel}>Tasso abbandono anticipato (&lt;80%)</div>
                          <div className={styles.metricValueWithTrend}>
                            <span className={styles.metricValue}>{formatPercent(stats.early_dropout_rate_percent)}</span>
                            {
                              dropoutTrendDirection === "up" &&
                              <span className={`${styles.trendIndicator} ${styles.trendDown}`}>
                                <span className={styles.trendArrow}>↑</span>
                                <span className={styles.trendLabel}>in aumento</span>
                              </span>
                            }
                            {
                              dropoutTrendDirection === "down" &&
                              <span className={`${styles.trendIndicator} ${styles.trendUp}`}>
                                <span className={styles.trendArrow}>↓</span>
                                <span className={styles.trendLabel}>in calo</span>
                              </span>
                            }
                            {
                              dropoutTrendDirection === "flat" &&
                              <span className={`${styles.trendIndicator} ${styles.trendNeutral}`}>
                                <span className={styles.trendArrow}>→</span>
                                <span className={styles.trendLabel}>stabile</span>
                              </span>
                            }
                          </div>
                          {
                            dropoutTrendChartData.length > 0 &&
                            <div className={styles.feedbackMiniChartContainer}>
                              <ResponsiveContainer width="100%" height={74}>
                                <LineChart data={dropoutTrendChartData} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                                  <Tooltip
                                    content={({ active, payload }) => {
                                      if (!active || !payload || payload.length === 0) return null

                                      const item = payload[0]?.payload
                                      if (!item) return null

                                      const valueText = Number.isFinite(Number(item.average_rate_percent))
                                        ? `${Number(item.average_rate_percent).toFixed(2)}%`
                                        : "-"

                                      return (
                                        <div style={{
                                          backgroundColor: "#ffffff",
                                          border: "1px solid rgba(0, 0, 0, 0.12)",
                                          borderRadius: "8px",
                                          padding: "8px 10px",
                                          fontSize: "12px",
                                          lineHeight: 1.35,
                                        }}>
                                          <div style={{ fontWeight: 700, textTransform: "capitalize" }}>
                                            {formatLongDayLabel(item.date)}
                                          </div>
                                          <div>{item.lessons_count} lezioni</div>
                                          <div>Tasso: {valueText}</div>
                                        </div>
                                      )
                                    }}
                                  />
                                  <Line type="monotone" dataKey="average_rate_percent" stroke="#1f6feb" strokeWidth={2.2} dot={false} connectNulls={true} />
                                  <Line type="monotone" dataKey="moving_average" stroke="#f59e0b" strokeWidth={2} strokeDasharray="4 3" dot={false} connectNulls={true} />
                                </LineChart>
                              </ResponsiveContainer>
                              <div className={styles.chartLegend}>
                                <span className={styles.chartLegendItem}>
                                  <span className={styles.chartLegendLine} />
                                  Andamento
                                </span>
                                <span className={styles.chartLegendItem}>
                                  <span className={`${styles.chartLegendLine} ${styles.chartLegendLineMovingAverage}`} />
                                  Media mobile
                                </span>
                              </div>
                            </div>
                          }
                        </div>

                        <div className={styles.metricCard}>
                          <div className={styles.metricLabel}>Presenza media per lezione</div>
                          <div className={styles.metricValueWithTrend}>
                            <span className={styles.metricValue}>{formatPercent(stats.average_attendance_rate_percent)}</span>
                            {
                              attendanceTrendDirection === "up" &&
                              <span className={`${styles.trendIndicator} ${styles.trendUp}`}>
                                <span className={styles.trendArrow}>↑</span>
                                <span className={styles.trendLabel}>in aumento</span>
                              </span>
                            }
                            {
                              attendanceTrendDirection === "down" &&
                              <span className={`${styles.trendIndicator} ${styles.trendDown}`}>
                                <span className={styles.trendArrow}>↓</span>
                                <span className={styles.trendLabel}>in calo</span>
                              </span>
                            }
                            {
                              attendanceTrendDirection === "flat" &&
                              <span className={`${styles.trendIndicator} ${styles.trendNeutral}`}>
                                <span className={styles.trendArrow}>→</span>
                                <span className={styles.trendLabel}>stabile</span>
                              </span>
                            }
                          </div>
                          {
                            attendanceTrendChartData.length > 0 &&
                            <div className={styles.feedbackMiniChartContainer}>
                              <ResponsiveContainer width="100%" height={74}>
                                <LineChart data={attendanceTrendChartData} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                                  <Tooltip
                                    content={({ active, payload }) => {
                                      if (!active || !payload || payload.length === 0) return null

                                      const item = payload[0]?.payload
                                      if (!item) return null

                                      const valueText = Number.isFinite(Number(item.average_rate_percent))
                                        ? `${Number(item.average_rate_percent).toFixed(2)}%`
                                        : "-"

                                      return (
                                        <div style={{
                                          backgroundColor: "#ffffff",
                                          border: "1px solid rgba(0, 0, 0, 0.12)",
                                          borderRadius: "8px",
                                          padding: "8px 10px",
                                          fontSize: "12px",
                                          lineHeight: 1.35,
                                        }}>
                                          <div style={{ fontWeight: 700, textTransform: "capitalize" }}>
                                            {formatLongDayLabel(item.date)}
                                          </div>
                                          <div>{item.lessons_count} lezioni</div>
                                          <div>Presenza: {valueText}</div>
                                        </div>
                                      )
                                    }}
                                  />
                                  <Line type="monotone" dataKey="average_rate_percent" stroke="#1f6feb" strokeWidth={2.2} dot={false} connectNulls={true} />
                                  <Line type="monotone" dataKey="moving_average" stroke="#f59e0b" strokeWidth={2} strokeDasharray="4 3" dot={false} connectNulls={true} />
                                </LineChart>
                              </ResponsiveContainer>
                              <div className={styles.chartLegend}>
                                <span className={styles.chartLegendItem}>
                                  <span className={styles.chartLegendLine} />
                                  Andamento
                                </span>
                                <span className={styles.chartLegendItem}>
                                  <span className={`${styles.chartLegendLine} ${styles.chartLegendLineMovingAverage}`} />
                                  Media mobile
                                </span>
                              </div>
                            </div>
                          }
                        </div>

                        <div className={styles.metricCard}>
                          <div className={styles.metricLabel}>Tempo medio di permanenza per studente</div>
                          <div className={styles.metricValue}>{formatDuration(stats.average_participation_seconds)}</div>
                        </div>

                        <div className={styles.metricCard}>
                          <div className={styles.metricLabel}>Ritorno studenti</div>
                          <div className={styles.metricValue}>{formatPercent(stats.returning_students_rate_percent)}</div>
                        </div>
                      </div>
                    </div>

                    <div className={styles.statsSection}>
                      <div className={styles.sectionTitle}>Feedback</div>
                      <div className={styles.metricsGrid}>
                        <div className={`${styles.metricCard} ${styles.feedbackTotalsCard}`}>
                          <div className={styles.metricLabel}>Feedback lezioni</div>
                          <div className={styles.feedbackTotalsGrid}>
                            <div className={styles.feedbackTotalsItem}>
                              <div className={styles.feedbackTotalsLabel}>Totali</div>
                              <div className={styles.feedbackTotalsValue}>{stats.lesson_feedbacks_count ?? 0}</div>
                            </div>
                            <div className={styles.feedbackTotalsItem}>
                              <div className={`${styles.feedbackTotalsLabel} ${styles.feedbackTotalsLabelPositive}`}>Positivi</div>
                              <div className={`${styles.feedbackTotalsValue} ${styles.scoreHigh}`}>{stats.positive_feedbacks_count ?? 0}</div>
                            </div>
                            <div className={styles.feedbackTotalsItem}>
                              <div className={`${styles.feedbackTotalsLabel} ${styles.feedbackTotalsLabelNeutral}`}>Neutri</div>
                              <div className={`${styles.feedbackTotalsValue} ${styles.scoreMedium}`}>{stats.neutral_feedbacks_count ?? 0}</div>
                            </div>
                            <div className={styles.feedbackTotalsItem}>
                              <div className={`${styles.feedbackTotalsLabel} ${styles.feedbackTotalsLabelNegative}`}>Negativi</div>
                              <div className={`${styles.feedbackTotalsValue} ${styles.scoreLow}`}>{stats.negative_feedbacks_count ?? 0}</div>
                            </div>
                          </div>
                        </div>

                        <div className={styles.metricCard}>
                          <div className={styles.metricLabel}>Punteggio Medio</div>
                          <div className={styles.metricValueWithTrend}>
                            <span className={`${styles.metricValue} ${getScoreClass(stats.average_lesson_feedback_score)}`}>
                              {formatScore(stats.average_lesson_feedback_score)}
                            </span>
                            {
                              feedbackTrendDirection === "up" &&
                              <span className={`${styles.trendIndicator} ${styles.trendUp}`}>
                                <span className={styles.trendArrow}>↑</span>
                                <span className={styles.trendLabel}>in aumento</span>
                              </span>
                            }
                            {
                              feedbackTrendDirection === "down" &&
                              <span className={`${styles.trendIndicator} ${styles.trendDown}`}>
                                <span className={styles.trendArrow}>↓</span>
                                <span className={styles.trendLabel}>in calo</span>
                              </span>
                            }
                            {
                              feedbackTrendDirection === "flat" &&
                              <span className={`${styles.trendIndicator} ${styles.trendNeutral}`}>
                                <span className={styles.trendArrow}>→</span>
                                <span className={styles.trendLabel}>stabile</span>
                              </span>
                            }
                          </div>

                          {
                            feedbackTrendChartData.length > 0 &&
                            <div className={styles.feedbackMiniChartContainer}>
                              <ResponsiveContainer width="100%" height={74}>
                                <LineChart data={feedbackTrendChartData} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                                  <Tooltip
                                    content={({ active, payload }) => {
                                      if (!active || !payload || payload.length === 0) return null

                                      const item = payload[0]?.payload
                                      if (!item) return null

                                      const averageText = Number.isFinite(Number(item.average_score))
                                        ? `${Number(item.average_score).toFixed(2)} / 5`
                                        : "-"

                                      return (
                                        <div style={{
                                          backgroundColor: "#ffffff",
                                          border: "1px solid rgba(0, 0, 0, 0.12)",
                                          borderRadius: "8px",
                                          padding: "8px 10px",
                                          fontSize: "12px",
                                          lineHeight: 1.35,
                                        }}>
                                          <div style={{ fontWeight: 700, textTransform: "capitalize" }}>
                                            {formatLongDayLabel(item.date)}
                                          </div>
                                          <div>{item.feedback_count} feedback</div>
                                          <div>Media: {averageText}</div>
                                        </div>
                                      )
                                    }}
                                  />
                                  <Line type="monotone" dataKey="average_score" stroke="#1f6feb" strokeWidth={2.2} dot={false} connectNulls={true} />
                                  <Line type="monotone" dataKey="moving_average" stroke="#f59e0b" strokeWidth={2} strokeDasharray="4 3" dot={false} connectNulls={true} />
                                </LineChart>
                              </ResponsiveContainer>
                              <div className={styles.chartLegend}>
                                <span className={styles.chartLegendItem}>
                                  <span className={styles.chartLegendLine} />
                                  Andamento
                                </span>
                                <span className={styles.chartLegendItem}>
                                  <span className={`${styles.chartLegendLine} ${styles.chartLegendLineMovingAverage}`} />
                                  Media mobile
                                </span>
                              </div>
                            </div>
                          }

                          {
                            (stats.average_lesson_feedback_score_by_question?.length ?? 0) > 0 &&
                            <div className={styles.feedbackQuestionsColumn}>
                              <div className={styles.feedbackQuestionsTitle}>Media voti per domanda (1-5)</div>
                              <Each
                                of={stats.average_lesson_feedback_score_by_question}
                                render={(item) => (
                                  <div className={styles.questionCard}>
                                    <div className={styles.questionText}>{item.question}</div>
                                    <div className={styles.questionMeta}>
                                      <span className={getScoreClass(item.average_score)}>{formatScore(item.average_score)}</span>
                                      <span>{item.answers_count} risposte</span>
                                    </div>
                                  </div>
                                )}
                              />
                            </div>
                          }

                        </div>
                      </div>

                      {
                        (stats.worst_lessons_feedback?.length ?? 0) > 0 &&
                        <div className={styles.metricCard}>
                          <div className={styles.metricLabel}>Lezioni Peggiori</div>
                          <div className={styles.worstLessonsCompactList}>
                            <Each
                              of={stats.worst_lessons_feedback}
                              render={(lesson) => (
                                <div className={styles.worstLessonCompactItem}>
                                  <div className={styles.worstLessonCompactTop}>
                                    <span className={styles.worstLessonCompactName}>{lesson.lesson_name}</span>
                                    <span className={`${styles.worstLessonScore} ${getScoreClass(lesson.average_score)}`}>
                                      {formatScore(lesson.average_score)}
                                    </span>
                                  </div>
                                  <div className={styles.worstLessonMeta}>
                                    {lesson.lesson_date ? `${formatLongDayLabel(lesson.lesson_date)} - ` : ""}{lesson.feedbacks_count} feedback
                                  </div>
                                  <Each
                                    of={lesson.average_score_by_question ?? []}
                                    render={(question) => (
                                      <div className={styles.worstLessonQuestionRow}>
                                        <span className={styles.worstLessonQuestionLabel}>{question.question}</span>
                                        <span className={getScoreClass(question.average_score)}>{formatScore(question.average_score)}</span>
                                      </div>
                                    )}
                                  />
                                </div>
                              )}
                            />
                          </div>
                        </div>
                      }
                    </div>
                  </div>
                }
              </div>
            </div>

            <Dialog appearance="modern"
              open={dialogOpen}
              style={{
                width: "100%",
                maxWidth: "512px"
              }}
              onClose={() => {
                setDialogOpen(false)
                setCropMode(false)
              }}
              title={"Immagine del profilo"}>
              <div className={styles.dialog}>
                {!cropMode &&
                  <Dropzone
                    onDrop={(files) => {
                      if (files[0]) {
                        setImgFile(files[0])
                        setCropMode(true)
                      }
                    }}
                    accept={{
                      'image/jpeg': [],
                      'image/png': [],
                      'image/heic': []
                    }}
                  >
                    {({ getRootProps, getInputProps }) => (
                      <section style={{ display: 'flex', width: '100%' }}>
                        <div {...getRootProps()} style={{ display: 'flex', width: '100%' }}>
                          <input {...getInputProps()} ref={dropzoneRef} />
                          <div className={styles.editPictureDropzone}>
                            <InsertImageIllustration></InsertImageIllustration>
                            <div className={styles.editPictureLabel}>
                              {t('profile.dropImage')}
                            </div>
                            <Button
                              style={{ marginTop: '.5rem', padding: '0.6rem 2rem' }}
                              appearance="subtle" accentColor="var(--primary)"
                              onClick={() => dropzoneRef.current?.click()}
                            >
                              Scegli immagine
                            </Button>
                          </div>
                        </div>
                      </section>
                    )}
                  </Dropzone>
                }
                {cropMode &&
                  <>
                    <ImageCropper
                      src={URL.createObjectURL(imgFile)}
                      onSave={(image) => {
                        setProfilePic(image)
                        setCropMode(false)
                        setDialogOpen(false)
                      }}
                      onCancel={() => { setCropMode(false) }}
                      compression={{ quality: 0.7, maxWidth: 300, maxHeight: 300 }}
                    >
                    </ImageCropper>
                  </>
                }
              </div>
            </Dialog>

            <Dialog overflow="visible" open={moduleDialog} onClose={() => {
              setModuleDialog(false)
              setSubscriptions({ course: { id: null, prefilled: false }, module: { id: null, prefilled: false }, edition: { id: null, prefilled: false } })
              setSelectedModules([])
              setEditions([])
              setModules([])
              setLoadingEditions(false)
              setLoadingModules(false)
            }} title={"Assegna modulo"}
              action={<Button disabled={!(subscriptions.course.id && subscriptions.edition.id)} status={loadingAssign ? ButtonStatus.Loading : null} onClick={assignToModule}>Salva</Button>}
            >
              <div className={styles.dialogContent}>
                <Select appearance="modern" portal
                  nullable={false}
                  placeholder={loadingCourses ? "Caricamento corsi..." : "Seleziona corso"}
                  options={courses.sort((a, b) => a.name.localeCompare(b.name))}
                  selected={courses.find(c => c.id === subscriptions.course.id) ?? null}
                  loading={loadingCourses}
                  disabled={subscriptions.course.prefilled || loadingCourses}
                  onSelect={async (item) => {
                    setSubscriptions({ course: { id: item.id, prefilled: false }, edition: { id: null, prefilled: false }, module: { id: null, prefilled: false } })
                    setSelectedModules([])
                    setEditions([])
                    setModules([])
                    await loadEditionsByCourse(item.id)
                  }}
                />
                {subscriptions.course.id &&
                  <Select appearance="modern" portal
                    nullable={false}
                    placeholder={loadingEditions ? "Caricamento edizioni..." : "Seleziona edizione"}
                    options={editions.filter(ed => ed.course_id === subscriptions.course.id).sort((a, b) => a.name.localeCompare(b.name))}
                    selected={editions.filter(ed => ed.course_id === subscriptions.course.id).find(ed => ed.id === subscriptions.edition.id) ?? null}
                    loading={loadingEditions}
                    disabled={loadingEditions}
                    onSelect={async (item) => {
                      setSubscriptions({ ...subscriptions, edition: { id: item.id, prefilled: false }, module: { id: null, prefilled: false } })
                      setSelectedModules([])
                      setModules([])
                      const modulesList = await loadModulesByEdition(subscriptions.course.id, item.id)

                      const alreadyAssigned = coursesModules
                        .flatMap((cm) => cm.modules)
                        .filter((m) => m.edition?.id === item.id)
                        .map((m) => m.id)

                      setSelectedModules(() => {
                        const nextIds = new Set(alreadyAssigned)
                        return modulesList.filter((moduleItem) => nextIds.has(moduleItem.id))
                      })
                    }}
                  />
                }
                {subscriptions.edition.id &&
                  <MultipleSelect appearance="modern" portal
                    placeholder={loadingModules ? "Caricamento moduli..." : "Seleziona moduli"}
                    options={modules.filter(m => m.edition_id === subscriptions.edition.id).sort(sortModulesByPosition)}
                    selected={selectedModules}
                    disabled={loadingModules}
                    onSelect={(items) => {
                      setSelectedModules(items)
                    }}
                  />
                }
              </div>

            </Dialog>

            <AlertDialog
              open={alert.open}
              title={alert.title}
              text={alert.message}
              onClose={alert.onClose ?? resetAlert}
              actions={alert.actions}
              status={alert.status}
            />
          </div>
        </div>
      </div>
    </HeaderFooterLayout >
  )
}

export default Teacher
